import { Redirect, Stack, useRouter, usePathname } from 'expo-router';
import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  appendLaunchParamsToHref,
  hrefPathname,
  useOnboardingSteps,
  useRulesEngine,
} from '@/modules/rulesEngine';
import {
  getOnboardingHeaderHeight,
  LoadingScreen,
  OnboardingHeader,
  ScreenTopInsetProvider,
} from '@/modules/ui';

// Matches Screen's MAX_CONTENT_WIDTH so the header column aligns with body
// content across the flow on wide viewports.
const MAX_HEADER_CONTENT_WIDTH = 600;

export default function OnboardingLayout() {
  const { currentRoute, isResolving } = useRulesEngine();
  const { canShowHeader, progress, canShowBack, goBack } = useOnboardingSteps();
  const pathname = usePathname();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  // One-way latch. Flipped once the URL agrees with the rules engine for
  // the first time. While false, a cold-start deep-link mismatch is handled
  // by returning <Redirect> below — that early-return prevents Stack (and
  // therefore the deep-linked screen) from mounting and firing its effects
  // before the URL gets corrected. After it flips true, the imperative
  // router.replace effect handles all subsequent navigation so the Header
  // stays mounted and the progress bar doesn't flicker on every Continue.
  const [routeReady, setRouteReady] = useState(false);

  // currentRoute may carry a `?org=` query or use the object Href form;
  // pathname is always the bare path. Compare on the bare path.
  const currentPath = hrefPathname(currentRoute);

  // Only reserve chrome space when this step actually shows the header.
  // /account-creation opts out (`canShowHeader: false` in the step config),
  // so we publish 0 and let Screen fall back to its own safe-area handling —
  // no wasted vertical band above the form. `getOnboardingHeaderHeight` has
  // exactly one caller (this file) today — it's pulled out purely so this
  // arithmetic isn't inlined here, not to keep two callers in sync.
  // `DynamicAssessmentFlow`'s own reservation was removed along with its
  // module-owned progress bar; a host (VRN) owns that reservation now, on its
  // own screen, with no shared helper.
  const reservedHeaderHeight = getOnboardingHeaderHeight({
    insetTop: insets.top,
    visible: canShowHeader,
  });

  // Post-cold-start forward navigation. Gated on routeReady so it doesn't
  // race with the cold-start <Redirect> below — once the URL has agreed
  // with the rules engine once, this effect owns every subsequent
  // redirect and the Header stays mounted continuously.
  useEffect(() => {
    if (routeReady && currentRoute && currentPath !== pathname) {
      // Re-append the embed launch params so vida_client / … persist in the URL
      // across the flow. Compared on the bare path (currentPath), so the added
      // params never re-trigger this replace.
      router.replace(appendLaunchParamsToHref(currentRoute));
    }
  }, [routeReady, currentRoute, currentPath, pathname, router]);

  // Latch routeReady the first time the URL agrees with the rules engine.
  useEffect(() => {
    if (currentPath === pathname) {
      setRouteReady(true);
    }
  }, [currentPath, pathname]);

  // Cold-start deep-link mismatch: declarative redirect so Stack doesn't
  // mount the stale route's screen even briefly. One-shot — once routeReady
  // is true we never re-enter this branch, so the Header only remounts the
  // single time the cold-start redirect resolves.
  if (!routeReady && !isResolving && currentRoute && currentPath !== pathname) {
    return <Redirect href={appendLaunchParamsToHref(currentRoute)} />;
  }

  return (
    <ScreenTopInsetProvider topInset={reservedHeaderHeight}>
      <View className='flex-1'>
        <Stack screenOptions={{ headerShown: false }} />
        {/* OnboardingHeader is presentational — the rules-engine wiring lives
            here so `@/modules/ui` never imports `@/modules/rulesEngine`. That
            edge put analytics (Datadog + PostHog) in the graph of every ui
            consumer, `@/modules/dynamicAssessments` included.

            Rendered UNCONDITIONALLY with `visible`, never mounted conditionally:
            the progress bar's Animated.Value lives inside the header, so
            unmounting it on a header-hidden step resets the fill to 0 and
            re-animates from empty on the next visible one. `canShowHeader` goes
            false mid-flow (the in-flight RTE eligibility check), not just on the
            opt-out steps, so that would be visible to a member.
            `getOnboardingHeaderHeight` returns 0 when hidden, so the reserved
            inset actually drops — a hidden-header step's body sits HIGHER,
            not at the same y-position a visible header would leave it at. */}
        <OnboardingHeader
          maxContentWidth={MAX_HEADER_CONTENT_WIDTH}
          paddingTop={insets.top}
          progress={progress}
          canShowBack={canShowBack}
          onBack={goBack}
          visible={canShowHeader}
        />
        {!routeReady || isResolving ? <LoadingScreen className='absolute inset-0' /> : null}
      </View>
    </ScreenTopInsetProvider>
  );
}
